import { useMemo } from 'react';
import { useNavigate, Link } from '@tanstack/react-router';
import { Route } from '@src/routes/support/support-guide-article';
import {
  GridContainer,
  Grid,
  Breadcrumb,
  BreadcrumbBar,
  Button,
  SummaryBox,
  SummaryBoxHeading,
  SummaryBoxContent,
  Alert,
  Tag,
} from '@trussworks/react-uswds';
import Icon from '@mdi/react';
import { mdiClockOutline, mdiCalendarOutline, mdiCheckCircle, mdiAlertCircle, mdiChevronRight } from '@mdi/js';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import articlesData from './data/articlesData.json';
import faqData from './data/faqData.json';
// import { useSupportWidget } from '@stores';
import './support.scss';

const SupportGuideArticle = () => {
  const navigate = useNavigate();
  const { slug } = Route.useSearch();

  const article = useMemo(() => {
    return articlesData.articles.find((a) => a.slug === slug);
  }, [slug]);

  // Get related FAQs
  const relatedFaqs = useMemo(() => {
    if (!article?.relatedFaqs) return [];
    const faqs = [];
    faqData.categories.forEach((category) => {
      category.items.forEach((item) => {
        if (article.relatedFaqs.includes(item.id)) {
          faqs.push({ ...item, categoryId: category.id });
        }
      });
    });
    return faqs;
  }, [article]);

  // Get related articles
  const relatedArticles = useMemo(() => {
    if (!article?.relatedArticles) return [];
    return article.relatedArticles.map((slug) => articlesData.articles.find((a) => a.slug === slug)).filter(Boolean);
  }, [article]);

  if (!article) {
    return (
      <div className='support-guide-article'>
        <GridContainer>
          <Alert type='error' heading='Article not found'>
            The requested guide could not be found.
            <Button type='button' onClick={() => navigate({ to: '/support/support-guides' })} className='alert-link'>
              Return to all guides
            </Button>
          </Alert>
        </GridContainer>
      </div>
    );
  }

  return (
    <div className='support-guide-article'>
      <GridContainer>
        {/* Breadcrumb */}
        <BreadcrumbBar>
          <Breadcrumb>
            <Link to='/support/' className='usa-breadcrumb__link'>
              {' '}
              Support{' '}
            </Link>
          </Breadcrumb>
          <Breadcrumb>
            <Link to='/support/support-guides' className='usa-breadcrumb__link'>
              {' '}
              Guides{' '}
            </Link>
          </Breadcrumb>
          <Breadcrumb current>
            <span>{article.title}</span>
          </Breadcrumb>
        </BreadcrumbBar>

        <Grid row gap='lg'>
          {/* Main Content */}
          <Grid desktop={{ col: 8 }} tablet={{ col: 12 }}>
            <article className='guide-article-content'>
              <header className='article-header'>
                <h1>{article.title}</h1>
                <div className='article-meta'>
                  <span className='meta-item'>
                    <Icon path={mdiClockOutline} size={0.8} />
                    {article.readTime} read
                  </span>
                  <span className='meta-item'>
                    <Icon path={mdiCalendarOutline} size={0.8} />
                    Updated {article.lastUpdated}
                  </span>
                </div>
                {article.tags && (
                  <div className='article-tags'>
                    {article.tags.map((tag) => (
                      <Tag key={tag}>{tag}</Tag>
                    ))}
                  </div>
                )}
              </header>

              {/* Summary Box / Table of Contents */}
              <SummaryBox className='article-summary-box'>
                <SummaryBoxHeading headingLevel='h3'>In This Article</SummaryBoxHeading>
                <SummaryBoxContent>
                  <ul className='article-toc'>
                    {article.sections.map((section, index) => (
                      <li key={index}>
                        <a href={`#section-${index}`}>{section.title}</a>
                      </li>
                    ))}
                  </ul>
                </SummaryBoxContent>
              </SummaryBox>

              {/* Requirements Alert (if applicable) */}
              {article.requirements && (
                <Alert type='warning' headingLevel='h3' heading='Before You Begin' className='requirements-alert'>
                  <table className='requirements-table'>
                    <tbody>
                      {article.requirements.map((req, index) => (
                        <tr key={index}>
                          <th>{req.label}</th>
                          <td>{req.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Alert>
              )}

              {/* Before You Begin (if applicable) */}
              {article.beforeYouBegin && (
                <div className='before-you-begin'>
                  <h2>Before You Begin</h2>
                  {article.beforeYouBegin.gather && (
                    <>
                      <h3>Gather These Items:</h3>
                      <ul className='checklist'>
                        {article.beforeYouBegin.gather.map((item, index) => (
                          <li key={index}>
                            <Icon path={mdiCheckCircle} size={0.8} className='checklist-icon' />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                  {article.beforeYouBegin.requirements && (
                    <>
                      <h3>System Requirements:</h3>
                      <ul className='checklist'>
                        {article.beforeYouBegin.requirements.map((item, index) => (
                          <li key={index}>
                            <Icon path={mdiCheckCircle} size={0.8} className='checklist-icon' />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </div>
              )}

              {/* Article Sections */}
              {article.sections.map((section, index) => (
                <section key={index} id={`section-${index}`} className='article-section'>
                  <h2>{section.title}</h2>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.content}</ReactMarkdown>
                </section>
              ))}

              {/* Common Errors (if applicable) */}
              {article.commonErrors && (
                <section className='article-section common-errors'>
                  <h2>Common Errors and Fixes</h2>
                  <div className='errors-list'>
                    {article.commonErrors.map((error, index) => (
                      <div key={index} className='error-item'>
                        <div className='error-header'>
                          <Icon path={mdiAlertCircle} size={1} className='error-icon' />
                          <strong>"{error.error}"</strong>
                        </div>
                        <div className='error-body'>
                          <p>
                            <strong>Cause:</strong> {error.cause}
                          </p>
                          <p>
                            <strong>Fix:</strong> {error.fix}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Checklist (if applicable) */}
              {article.checklist && (
                <section className='article-section checklist-section'>
                  <h2>Quick Reference Checklist</h2>
                  <ul className='final-checklist'>
                    {article.checklist.map((item, index) => (
                      <li key={index}>
                        <input type='checkbox' id={`check-${index}`} />
                        <label htmlFor={`check-${index}`}>{item}</label>
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </article>
          </Grid>

          {/* Sidebar */}
          <Grid desktop={{ col: 4 }} tablet={{ col: 12 }} className='guide-sidebar'>
            {/* Related FAQs */}
            {relatedFaqs.length > 0 && (
              <div className='sidebar-section'>
                <h3>Related FAQs</h3>
                <ul className='related-list'>
                  {relatedFaqs.map((faq) => (
                    <li key={faq.id}>
                      <button
                        className='related-link'
                        onClick={() => navigate({ to: '/support/support-faq', hash: faq.categoryId })}
                      >
                        {faq.question}
                        <Icon path={mdiChevronRight} size={0.7} />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Related Articles */}
            {relatedArticles.length > 0 && (
              <div className='sidebar-section'>
                <h3>Related Guides</h3>
                <ul className='related-list'>
                  {relatedArticles.map((relArticle) => (
                    <li key={relArticle.slug}>
                      <Button
                        className='related-link'
                        onClick={() =>
                          navigate({
                            to: '/support/support-guide-article',
                            search: { slug: relArticle.slug },
                          })
                        }
                      >
                        {relArticle.title}
                        <Icon path={mdiChevronRight} size={0.7} />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Need More Help */}
            <div className='sidebar-section need-help'>
              <h3>Need More Help?</h3>
              <p>Can't find what you're looking for?</p>
              {/* <Button type='button' onClick={openTicketModal}>
                  Contact Support
                </Button> */}
            </div>
          </Grid>
        </Grid>
      </GridContainer>
    </div>
  );
};

export default SupportGuideArticle;
